import { useEffect } from "react";
import { ArrowLeft, ArrowRight, BarChart3, Check, Database, Layers3 } from "lucide-react";

const stages = [
  ["Accounts", "Keep each applicant, family member or entity in a separate account so every application has a clear owner."],
  ["Applications", "Record the IPO name, application date, quantity, price, amount and linked account in one register."],
  ["Allotment", "Update allotted or not-allotted status and allotted quantity without losing the original application record."],
  ["Listing & sales", "Add listing information and sale details so invested capital and outcomes remain traceable."],
  ["Review", "Compare account, IPO, monthly and yearly performance using the same underlying records."],
];

const checklist = [
  "Create one consistent account name for every applicant",
  "Record each application when it is submitted",
  "Keep application amount and quantity together",
  "Update allotment status instead of deleting unsuccessful applications",
  "Record listing and sale information against the original application",
  "Review totals, allocation rate and profit/loss periodically",
  "Export and back up the database before major changes",
];

const faqs = [
  ["What is an IPO portfolio tracker?", "An IPO portfolio tracker is a structured record of applications, applicant accounts, allotments, listings, sales and resulting profit or loss."],
  ["Can YIIS-100 track multiple IPO applicant accounts?", "Yes. YIIS-100 links IPO applications to separate accounts and consolidates them for portfolio-level analytics and reports."],
  ["Is YIIS-100 a stock recommendation service?", "No. YIIS-100 is record-management and analytics software. It does not provide investment recommendations or guarantee allotment or returns."],
  ["Where is YIIS-100 data stored?", "YIIS-100 is designed as a Windows desktop application with a local application database. Users should maintain regular backups."],
];

export default function IpoTrackerGuide() {
  useEffect(() => {
    const previousTitle = document.title;
    const description = document.querySelector('meta[name="description"]');
    const canonical = document.querySelector('link[rel="canonical"]');
    const previousDescription = description?.getAttribute("content");
    const previousCanonical = canonical?.getAttribute("href");

    document.title = "IPO Portfolio Tracker for Multiple Accounts | YIIS-100";
    description?.setAttribute("content", "Learn how to track IPO applications, allotments, listings and profit/loss across multiple accounts with a consistent IPO portfolio workflow.");
    canonical?.setAttribute("href", "https://yiis100.com/ipo-portfolio-tracker");

    const schema = document.createElement("script");
    schema.type = "application/ld+json";
    schema.dataset.yiisSeo = "ipo-tracker-guide";
    schema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map(([question, answer]) => ({
        "@type": "Question",
        name: question,
        acceptedAnswer: { "@type": "Answer", text: answer },
      })),
    });
    document.head.appendChild(schema);
    window.scrollTo(0, 0);

    return () => {
      document.title = previousTitle;
      if (previousDescription) description?.setAttribute("content", previousDescription);
      if (previousCanonical) canonical?.setAttribute("href", previousCanonical);
      schema.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground font-sans" data-testid="ipo-tracker-guide-page">
      <header className="sticky top-0 z-50 border-b border-border bg-background/90 backdrop-blur-xl">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 font-mono text-xs tracking-[0.18em] uppercase text-muted-foreground hover:text-accent">
            <ArrowLeft size={14} /> YIIS-100
          </a>
          <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">IPO Tracking Guide</span>
        </div>
      </header>

      <main>
        <section className="max-w-[1100px] mx-auto px-6 md:px-12 pt-20 pb-16">
          <div className="font-mono text-xs tracking-[0.24em] uppercase text-accent mb-5">Practical guide · India</div>
          <h1 className="font-display font-semibold tracking-tight leading-[0.95] text-4xl sm:text-6xl md:text-7xl max-w-4xl">
            IPO PORTFOLIO TRACKER FOR MULTIPLE ACCOUNTS
          </h1>
          <p className="mt-7 text-base md:text-xl leading-relaxed text-muted-foreground max-w-3xl">
            A reliable IPO record should connect every application to its applicant account, preserve unsuccessful applications, and carry allotted shares through listing, sale and portfolio review. This guide explains a repeatable workflow.
          </p>
        </section>

        <section className="border-y border-border bg-muted/30">
          <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-16 grid md:grid-cols-3 gap-10">
            {[
              [Layers3, "One lifecycle", "Application → allotment → listing → sale"],
              [Database, "One source", "Consistent records across applicant accounts"],
              [BarChart3, "Useful review", "Account, IPO, monthly and yearly analysis"],
            ].map(([Icon, title, body]) => (
              <div key={title}>
                <Icon size={22} className="text-accent mb-4" />
                <h2 className="font-display text-xl font-semibold mb-2">{title}</h2>
                <p className="text-sm leading-relaxed text-muted-foreground">{body}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="max-w-[1100px] mx-auto px-6 md:px-12 py-20">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <div className="font-mono text-xs tracking-[0.22em] uppercase text-accent mb-4">The workflow</div>
              <h2 className="font-display text-3xl md:text-4xl font-semibold">How to track an IPO from application to outcome</h2>
            </div>
            <div className="md:col-span-8 border-t border-border">
              {stages.map(([title, body], index) => (
                <article key={title} className="grid grid-cols-[48px_1fr] gap-4 py-7 border-b border-border">
                  <span className="font-mono text-xs text-accent">0{index + 1}</span>
                  <div>
                    <h3 className="font-display text-xl font-semibold mb-2">{title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{body}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-foreground text-background">
          <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-20 grid md:grid-cols-2 gap-14">
            <div>
              <div className="font-mono text-xs tracking-[0.22em] uppercase text-accent mb-4">Tracking checklist</div>
              <h2 className="font-display text-3xl md:text-4xl font-semibold">Keep the record complete</h2>
            </div>
            <ul className="space-y-4">
              {checklist.map((item) => (
                <li key={item} className="flex gap-3 leading-relaxed"><Check size={17} className="text-accent shrink-0 mt-1" />{item}</li>
              ))}
            </ul>
          </div>
        </section>

        <section className="max-w-[1100px] mx-auto px-6 md:px-12 py-20">
          <div className="font-mono text-xs tracking-[0.22em] uppercase text-accent mb-4">Frequently asked questions</div>
          <h2 className="font-display text-3xl md:text-4xl font-semibold mb-10">IPO tracking basics</h2>
          <div className="border-t border-border">
            {faqs.map(([question, answer]) => (
              <article key={question} className="grid md:grid-cols-12 gap-4 py-7 border-b border-border">
                <h3 className="md:col-span-5 font-display text-lg font-semibold">{question}</h3>
                <p className="md:col-span-7 text-muted-foreground leading-relaxed">{answer}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="max-w-[1100px] mx-auto px-6 md:px-12 pb-24">
          <div className="border border-border p-8 md:p-12">
            <div className="font-mono text-xs tracking-[0.22em] uppercase text-accent mb-4">YIIS-100 · IPO Intelligence Edition</div>
            <h2 className="font-display text-3xl md:text-5xl font-semibold max-w-3xl">Move beyond scattered IPO spreadsheets</h2>
            <p className="mt-5 text-muted-foreground leading-relaxed max-w-2xl">YIIS-100 brings accounts, applications, allotments, listings, profit/loss, analytics and reports into one Windows desktop workflow.</p>
            <a href="/" className="mt-8 inline-flex items-center gap-3 px-6 py-3 bg-accent text-accent-foreground font-mono text-xs tracking-[0.16em] uppercase font-bold">
              Explore YIIS-100 <ArrowRight size={15} />
            </a>
          </div>
          <p className="mt-8 text-xs leading-relaxed text-muted-foreground">Educational information only. YIIS-100 is a record-management and analytics tool, not investment advice or an allotment/return guarantee.</p>
        </section>
      </main>
    </div>
  );
}